/**
 * Iter 95 — Patient-facing Trust Profile.
 *
 * Friendly dashboard at /patient/trust showing the patient how their clinic
 * stewards their data. Couples GDPR compliance to retention.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { t } from '../lib/i18n';
import {
  Lock, ShieldCheck, Mail, Download, Database, Building2,
  CheckCircle2, XCircle, ArrowRight, Clock, MapPin,
} from 'lucide-react';

export default function PatientTrustProfile() {
  const { locale } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/patient/me/trust-profile')
      .then(r => setData(r.data))
      .catch(e => {
        if (e?.response?.status === 401) {
          nav('/login', { state: { from: '/patient/trust' } });
          return;
        }
        setErr(e?.response?.data?.detail || 'Could not load trust profile');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) return <div className="min-h-screen flex items-center justify-center text-fg-muted">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-fg-muted">Loading…</div>;

  const pf = data.privacy_first || {};
  const tp = data.transparency || {};
  const cons = data.consents || {};
  const acc = data.data_access || {};
  const me = data.my_footprint || {};

  return (
    <div className="min-h-screen bg-[#FAF6EE] pb-20" data-testid="patient-trust-profile">
      {/* Hero */}
      <header className="bg-[#003D80] text-white py-10 md:py-14 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FFB000] font-medium inline-flex items-center gap-1.5">
            <Lock className="w-3 h-3"/> YOUR TRUST PROFILE
          </div>
          <h1 className="text-white text-3xl md:text-4xl font-heading font-bold mt-2 leading-tight" data-testid="trust-profile-h1">
            How <span className="text-[#FFB000]">{data.clinic.name}</span> looks after your data
          </h1>
          <p className="text-white/80 mt-3 text-base max-w-2xl">
            A friendly summary of what your clinic does — and doesn't do — with the information you've shared during your care.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-8 space-y-6">
        {/* Hero badge — Privacy-First earned */}
        {pf.earned ? (
          <div className="surface border-2 border-[#5DBB66]/60 bg-[#1B5E20]/5 rounded-2xl p-6 flex items-center gap-5" data-testid="pf-earned-card">
            <div className="w-16 h-16 rounded-2xl bg-[#1B5E20]/15 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#1B5E20]"/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-xl text-[#1B5E20]">Privacy-First clinic — {pf.score}/100</div>
              <p className="text-sm text-fg-muted mt-1">
                Your clinic meets {pf.criteria.filter(c => c.met).length} of {pf.criteria.length} privacy criteria. Patient rights under GDPR are fully self-service here.
              </p>
            </div>
          </div>
        ) : (
          <div className="surface border-2 border-[#F2B100]/60 bg-[#FFB000]/5 rounded-2xl p-6 flex items-center gap-5" data-testid="pf-pending-card">
            <div className="w-16 h-16 rounded-2xl bg-[#F2B100]/15 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#F2B100]"/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-xl">Privacy score: {pf.score}/100</div>
              <p className="text-sm text-fg-muted mt-1">
                Your clinic meets {pf.criteria.filter(c => c.met).length} of {pf.criteria.length} criteria. They're working toward the Privacy-First badge.
              </p>
            </div>
          </div>
        )}

        {/* Headline stats grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="trust-stats-grid">
          <StatCard icon={Mail} label="Transparency emails" value={tp.emails_received}
                    sub={tp.last_email_at ? `Last: ${new Date(tp.last_email_at).toLocaleDateString()}` : 'Not yet sent'}
                    testid="stat-transparency"/>
          <StatCard icon={CheckCircle2} label="Consent preferences" value={`${cons.configured_count} / ${cons.total_available}`}
                    sub="You're in control" testid="stat-consents"/>
          <StatCard icon={Download} label="Data exports" value={acc.export_count}
                    sub={acc.export_count ? 'Run any time' : 'Available on demand'}
                    testid="stat-exports"/>
          <StatCard icon={Database} label="Records held" value={me.records_held}
                    sub={me.account_age_days != null ? `Patient since ${me.account_age_days}d ago` : ''}
                    testid="stat-records"/>
        </div>

        {/* Criteria breakdown */}
        <section className="surface border border-soft rounded-xl p-5" data-testid="criteria-breakdown">
          <h2 className="font-heading font-semibold mb-1">Your rights — at a glance</h2>
          <p className="text-xs text-fg-muted mb-4">Every clinic on Physiogenic must offer these GDPR self-service controls. Here's how yours stacks up.</p>
          <ul className="space-y-2.5">
            {pf.criteria.map(c => (
              <li key={c.key} className="flex items-start gap-3 text-sm" data-testid={`criterion-${c.key}`}>
                {c.met
                  ? <CheckCircle2 className="w-5 h-5 text-[#1B5E20] shrink-0 mt-0.5"/>
                  : <XCircle className="w-5 h-5 text-[#C62828] shrink-0 mt-0.5"/>}
                <span className={c.met ? '' : 'text-fg-muted'}>{c.label}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Action panel */}
        <section className="grid md:grid-cols-2 gap-4">
          <ActionCard
            icon={Download}
            title="Download my data"
            body="Get a JSON file with every record we hold about you (GDPR Art. 20)."
            link="/patient/account#data-export-zone"
            cta="Open Account & Privacy"
            testid="action-download"/>
          <ActionCard
            icon={Lock}
            title="Manage my consents"
            body="Toggle 7 individual data uses on or off — withdraw any time (GDPR Art. 7)."
            link="/patient/account#consent-prefs-zone"
            cta="Manage 7 consents"
            testid="action-consents"/>
        </section>

        {/* Clinic context */}
        <section className="surface border border-soft rounded-xl p-5 text-sm" data-testid="clinic-context">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-brand shrink-0 mt-0.5"/>
            <div>
              <div className="font-semibold">{data.clinic.name}</div>
              <div className="text-fg-muted mt-0.5 flex items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> Data stored in {me.data_residency}</span>
                {me.account_age_days != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> Your account: {me.account_age_days}d old</span>}
              </div>
              {data.clinic.slug && (
                <Link to={`/c/${data.clinic.slug}`} className="text-brand text-xs hover:underline inline-flex items-center gap-1 mt-2">
                  Visit your clinic's public page <ArrowRight className="w-3 h-3"/>
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="surface border border-soft rounded-xl p-4" data-testid={testid}>
      <Icon className="w-4 h-4 text-brand mb-2"/>
      <div className="text-2xl font-heading font-bold leading-none">{value ?? '—'}</div>
      <div className="text-xs text-fg-muted mt-1">{label}</div>
      {sub && <div className="text-[10px] text-fg-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function ActionCard({ icon: Icon, title, body, link, cta, testid }) {
  return (
    <Link to={link} className="surface border border-soft rounded-xl p-5 hover:border-brand transition block" data-testid={testid}>
      <Icon className="w-5 h-5 text-brand mb-2"/>
      <div className="font-heading font-semibold">{title}</div>
      <p className="text-sm text-fg-muted mt-1 mb-3">{body}</p>
      <div className="text-sm text-brand font-medium inline-flex items-center gap-1">{cta} <ArrowRight className="w-4 h-4"/></div>
    </Link>
  );
}
